import {find, map} from 'lodash';
import {useEffect, useMemo, useState} from 'react';

import {sessionHistoryApi} from 'api';
import {MessagesModal} from 'components';
import {useApiData, useObservable, usePrevious} from 'hooks';
import {ConversationSummaryActivityData, SessionActivity, SessionActivityName, SessionHistory} from 'interfaces';
import {authDataStore} from 'services';
import {TextChatThreads} from 'types';
import {sessionUtils} from 'utils';

type Props = {
    open: boolean;
    session?: SessionHistory;
    activity?: SessionActivity;
    onClose: (params?: {ibm?: {sessionHistoryKey: string}}) => void;
}

export const ActivityMessagesModal: React.FC<Props> = ({open, session, activity, onClose}) => {
    const {localUser} = useObservable(authDataStore);
    const prevSessionId = usePrevious(session?.sessionId);
    const [selectedLanguage, setSelectedLanguage] = useState(null);

    const fetchFn = useMemo(() => {
        switch (activity?.name) {
            case SessionActivityName.SIP: return sessionHistoryApi.getSessionHistorySIPChat;
            case SessionActivityName.TextChat: return sessionHistoryApi.getSessionHistoryChat;
            case SessionActivityName.Avatar: return sessionHistoryApi.getAvatarHistoryChat;
            case SessionActivityName.AgentChatbot: return sessionHistoryApi.getAgentChatbotHistoryChat;
            case SessionActivityName.Audio:
            case SessionActivityName.AudioVideo: return sessionHistoryApi.getSessionHistoryAudioTranscription;
        }
    }, [activity]);
    const {send: getSessionHistoryChat, data: sessionWithMessages, loading, error} = useApiData(fetchFn);

    const activityWithMessages = useMemo(() => find(sessionWithMessages?.activities, {id: activity?.id}), [sessionWithMessages, activity]);

    const messages = useMemo(() => {
        let threads: TextChatThreads = {};
        if ([
            SessionActivityName.SIP, SessionActivityName.TextChat, SessionActivityName.Avatar, SessionActivityName.AgentChatbot
        ].includes(activity?.name)) {
            threads = activityWithMessages?.data?.threads;
        }
        if ([SessionActivityName.Audio, SessionActivityName.AudioVideo].includes(activity?.name)) {
            threads = activityWithMessages?.data?.transcription;
        }
        const result = sessionUtils.getTextChatActivityMessages(threads);
        if (!selectedLanguage) {
            return result;
        } else {
            const translations = activityWithMessages?.data?.translation?.[selectedLanguage];

            return map(result, (m) => {
                const translatedMessage = translations?.[m.from.id]?.[m.id];
                return {
                    ...m,
                    content: translatedMessage?.content || m.content
                };
            });

        }
    }, [
        activity?.name, activityWithMessages?.data?.translation,
        activityWithMessages?.data?.threads, selectedLanguage,
        activityWithMessages?.data?.transcription
    ]);

    const conversationSummary = useMemo(() =>
        (activityWithMessages?.data as ConversationSummaryActivityData)?.conversationSummary, [activityWithMessages]);

    const hideConversationSummary = useMemo(() => activity?.name === SessionActivityName.AgentChatbot, [activity?.name]);

    const integrations = useMemo(() => activityWithMessages?.data?.integrations, [activityWithMessages]);

    const userParticipantIds = useMemo(() => sessionUtils.getUserParticipantIdGroups(session), [session]);

    const localParticipantId = useMemo(() =>
        find(session?.participants, (p) => p.data?.eid === localUser.eid)?.id, [session, localUser]);

    const availableLanguages = useMemo(() => {
        const data = activityWithMessages?.data;
        if (data?.translation) {
            return Object.keys(data.translation);
        }
        return [];
    }, [activityWithMessages]);

    useEffect(() => {
        if (session?.sessionId && session?.sessionId !== prevSessionId) {
            getSessionHistoryChat(session?.sessionId, activity?.id);
        }
    }, [session?.sessionId, prevSessionId, getSessionHistoryChat, activity?.id]);

    return (
        <MessagesModal
            open={open}
            participants={session?.participants}
            isError={!!error}
            isLoading={loading}
            sessionId={session?.sessionId}
            activityName={activity?.name}
            activityId={activity?.id}
            messages={messages}
            conversationSummary={conversationSummary}
            hideConversationSummary={hideConversationSummary}
            selectedLanguage={selectedLanguage}
            availableLanguages={availableLanguages}
            integrations={integrations}
            localParticipantId={localParticipantId}
            userParticipantIds={userParticipantIds}
            onClose={onClose}
            onLanguageChange={setSelectedLanguage}
        />
    );
};